import { useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { dateDuJour, formaterDate, formaterIdentite } from '@gsm/partage';
import { api, ErreurApi } from '../api/client';
import { messageErreur, useRessource } from '../api/ressource';
import { useAuth } from '../auth/contexte';
import { Bloc, Tableau, type Colonne } from '../composants/Tableau';
import { BandeauAlerte } from '../composants/BandeauAlerte';
import { Champ } from '../composants/Champ';
import { Modale } from '../composants/Modale';
import { Nombre } from '../composants/Nombre';
import { Pastille, type TonPastille } from '../composants/Pastille';
import { LienFiche } from '../composants/LienAction';
import { MenuActions } from '../composants/MenuActions';
import { Note } from '../composants/Note';

interface Demande {
  id: string;
  reference: string;
  mandatId: string;
  montantDemande: string;
  totalite: boolean;
  motif: string | null;
  statut: 'OUVERTE' | 'ORDRES_GENERES' | 'SERVIE' | 'ABANDONNEE';
  dateDemande: string;
  dateServie: string | null;
  mandat: { reference: string; client: { nom: string; prenom: string } };
  ordres: { reference: string; statut: string }[];
}

interface MandatChoix {
  id: string;
  reference: string;
  statut: string;
  client: { nom: string; prenom: string };
}

const EN_COURS = new Set(['OUVERTE', 'ORDRES_GENERES']);

/**
 * Où en est une demande en cours, lu sur ses ordres. L'étape la moins avancée
 * l'emporte : un seul ordre resté en brouillon retient toute la sortie.
 */
export function etapeSortie(d: Pick<Demande, 'statut' | 'ordres'>): {
  libelle: string;
  ton: TonPastille;
} {
  if (d.statut === 'SERVIE') return { libelle: 'Servie', ton: 'succes' };
  if (d.statut === 'ABANDONNEE') return { libelle: 'Abandonnée', ton: 'neutre' };
  if (d.statut === 'OUVERTE') return { libelle: 'Proposition à générer', ton: 'attente' };
  const a = (s: string) => d.ordres.some((o) => o.statut === s);
  if (a('BROUILLON')) return { libelle: 'Ordres à soumettre', ton: 'attente' };
  if (a('A_VALIDER')) return { libelle: 'À valider', ton: 'attente' };
  if (a('VALIDE')) return { libelle: 'À transmettre', ton: 'interface' };
  return { libelle: 'Retrait attendu', ton: 'interface' };
}

const VIDE = { mandatId: '', montant: '', totalite: false, motif: '' };

/**
 * Sorties de fonds : le gérant choisit un mandat et libère un montant ou la
 * totalité ; l'écran suit ensuite chaque demande jusqu'au retrait constaté.
 *
 * Le montant saisi ici est le seul de toute la chaîne : il est enregistré sur
 * la demande, et la proposition le relit en base — jamais à l'écran.
 */
export function SortiesFonds() {
  const { aRole } = useAuth();
  const peutGerer = aRole('GERANT', 'RESP_GESTION');
  const [requete] = useSearchParams();

  const demandes = useRessource<Demande[]>('/demandes-sortie');
  const mandats = useRessource<MandatChoix[]>(peutGerer ? '/mandats' : null);

  const [saisie, setSaisie] = useState(() => ({
    ...VIDE,
    mandatId: requete.get('mandatId') ?? '',
  }));
  const [occupe, setOccupe] = useState(false);
  const [erreurSaisie, setErreurSaisie] = useState<string | null>(null);
  const [ouverte, setOuverte] = useState<string | null>(null);

  const [aAbandonner, setAAbandonner] = useState<Demande | null>(null);
  const [motifAbandon, setMotifAbandon] = useState('');
  const [erreurAbandon, setErreurAbandon] = useState<string | null>(null);

  const toutes = demandes.donnees ?? [];
  const enCours = toutes.filter((d) => EN_COURS.has(d.statut));
  const soldees = toutes.filter((d) => !EN_COURS.has(d.statut));
  const occupes = new Set(enCours.map((d) => d.mandatId));

  // Une sortie ne s'engage que sur un mandat en gestion (RG-06), et un seul
  // à la fois : le serveur le refuserait, l'écran ne le propose pas.
  const eligibles = (mandats.donnees ?? [])
    .filter((m) => m.statut === 'ACTIF' || m.statut === 'EN_LIQUIDATION')
    .sort((a, b) => a.reference.localeCompare(b.reference));

  async function ouvrir(evenement: { preventDefault: () => void }) {
    evenement.preventDefault();
    setErreurSaisie(null);
    setOuverte(null);
    setOccupe(true);
    try {
      const cree = await api.post<{ reference: string }>('/demandes-sortie', {
        mandatId: saisie.mandatId,
        ...(saisie.totalite ? { totalite: true } : { montantDemande: saisie.montant }),
        ...(saisie.motif.trim() ? { motif: saisie.motif.trim() } : {}),
        dateDemande: dateDuJour(),
      });
      setOuverte(cree.reference);
      setSaisie(VIDE);
      await demandes.recharger();
    } catch (e) {
      setErreurSaisie(e instanceof ErreurApi ? messageErreur(e) : 'Ouverture impossible.');
    } finally {
      setOccupe(false);
    }
  }

  async function abandonner(evenement: { preventDefault: () => void }) {
    evenement.preventDefault();
    if (!aAbandonner) return;
    setErreurAbandon(null);
    setOccupe(true);
    try {
      await api.post(`/demandes-sortie/${aAbandonner.id}/abandonner`, {
        motif: motifAbandon.trim(),
      });
      setAAbandonner(null);
      await demandes.recharger();
    } catch (e) {
      setErreurAbandon(e instanceof ErreurApi ? messageErreur(e) : 'Abandon impossible.');
    } finally {
      setOccupe(false);
    }
  }

  const montant = (d: Demande) => (d.totalite ? 'Totalité' : <Nombre valeur={d.montantDemande} />);

  const communes: Colonne<Demande>[] = [
    { cle: 'ref', entete: 'Référence', rendu: (d) => d.reference },
    {
      cle: 'mandat',
      entete: 'Mandat',
      num: true,
      rendu: (d) => <LienFiche to={`/mandats/${d.mandatId}`}>{d.mandat.reference}</LienFiche>,
    },
    {
      cle: 'client',
      entete: 'Client',
      rendu: (d) => formaterIdentite(d.mandat.client.nom, d.mandat.client.prenom),
    },
    {
      cle: 'date',
      entete: 'Demandée le',
      num: true,
      rendu: (d) => formaterDate(d.dateDemande),
    },
    { cle: 'montant', entete: 'Montant', num: true, rendu: montant },
    {
      cle: 'etape',
      entete: 'État',
      rendu: (d) => {
        const e = etapeSortie(d);
        return <Pastille ton={e.ton}>{e.libelle}</Pastille>;
      },
    },
  ];

  const colonnesEnCours: Colonne<Demande>[] = [
    ...communes,
    {
      cle: 'suite',
      entete: <span className="visuellement-cache">Suite</span>,
      num: true,
      rendu: (d) =>
        d.statut === 'OUVERTE' ? (
          <LienFiche to={`/mandats/${d.mandatId}/proposition`}>Proposition</LienFiche>
        ) : (
          <LienFiche to={`/ordres?mandatId=${d.mandatId}`}>Ordres</LienFiche>
        ),
    },
    ...(peutGerer
      ? [
          {
            cle: 'actions',
            entete: <span className="visuellement-cache">Actions</span>,
            num: true,
            rendu: (d: Demande) => (
              <MenuActions
                libelle={`Actions sur ${d.reference}`}
                groupes={[
                  [
                    {
                      libelle: 'Abandonner la demande',
                      icone: 'fermer',
                      surClic: () => {
                        setAAbandonner(d);
                        setMotifAbandon('');
                        setErreurAbandon(null);
                      },
                    },
                  ],
                ]}
              />
            ),
          },
        ]
      : []),
  ];

  const colonnesSoldees: Colonne<Demande>[] = [
    ...communes,
    {
      cle: 'servie',
      entete: 'Servie le',
      num: true,
      rendu: (d) => (d.dateServie ? formaterDate(d.dateServie) : '—'),
    },
    { cle: 'motif', entete: 'Motif', rendu: (d) => d.motif ?? '—' },
  ];

  return (
    <>
      <p className="entete__fil">Gestion</p>
      <div className="entete">
        <h1>Sorties de fonds</h1>
        <span className="entete__ref">{enCours.length} demande(s) en cours</span>
      </div>

      {demandes.erreur && (
        <BandeauAlerte nature="refus" titre="Chargement impossible">
          {messageErreur(demandes.erreur)}
        </BandeauAlerte>
      )}

      {peutGerer && (
        <Bloc titre="Libérer des fonds" meta="un mandat, un montant ou la totalité">
          {erreurSaisie && (
            <BandeauAlerte nature="refus" titre="Ouverture refusée">
              {erreurSaisie}
            </BandeauAlerte>
          )}
          {ouverte && (
            <BandeauAlerte nature="information" titre={`Demande ${ouverte} ouverte`}>
              Elle figure ci-dessous parmi les demandes en cours : générez sa proposition pour
              préparer les ventes.
            </BandeauAlerte>
          )}
          <form onSubmit={(e) => void ouvrir(e)} noValidate>
            <div className="champs-en-ligne">
              <div className="champ champ--double">
                <label className="surtitre" htmlFor="mandat-sortie">
                  Mandat
                </label>
                <select
                  id="mandat-sortie"
                  className="champ__select"
                  value={saisie.mandatId}
                  onChange={(e) => setSaisie({ ...saisie, mandatId: e.target.value })}
                  required
                >
                  <option value="">—</option>
                  {eligibles.map((m) => (
                    <option key={m.id} value={m.id} disabled={occupes.has(m.id)}>
                      {m.reference} — {formaterIdentite(m.client.nom, m.client.prenom)}
                      {occupes.has(m.id) ? ' (sortie en cours)' : ''}
                    </option>
                  ))}
                </select>
              </div>
              {!saisie.totalite && (
                <Champ
                  label="Montant à libérer (MAD)"
                  montant
                  inputMode="decimal"
                  value={saisie.montant}
                  onChange={(e) => setSaisie({ ...saisie, montant: e.target.value })}
                  required
                />
              )}
            </div>
            <label className="case-a-cocher">
              <input
                type="checkbox"
                checked={saisie.totalite}
                onChange={(e) => setSaisie({ ...saisie, totalite: e.target.checked })}
              />
              Liquidation totale du mandat
            </label>
            <div className="barre-actions barre-actions--champs">
              <div className="champ champ--large">
                <label className="surtitre" htmlFor="motif-sortie">
                  Motif
                </label>
                <input
                  id="motif-sortie"
                  value={saisie.motif}
                  maxLength={300}
                  onChange={(e) => setSaisie({ ...saisie, motif: e.target.value })}
                  placeholder="Facultatif"
                />
              </div>
              <button
                className="bouton"
                type="submit"
                disabled={
                  occupe || !saisie.mandatId || (!saisie.totalite && !saisie.montant.trim())
                }
              >
                {occupe ? 'En cours…' : 'Ouvrir la demande'}
              </button>
            </div>
          </form>
          <p className="note">
            Le montant ne peut excéder l’actif net, et le mandat doit avoir reçu une situation. Une
            liquidation totale retient l’actif net du jour.
          </p>
        </Bloc>
      )}

      <Bloc
        titre="Demandes en cours"
        meta={demandes.chargement ? 'chargement…' : 'de la proposition au retrait'}
      >
        <Tableau
          colonnes={colonnesEnCours}
          lignes={enCours}
          cle={(d) => d.id}
          vide="Aucune sortie de fonds en cours."
        />
        <Note resume="Une demande se solde d’elle-même au retrait constaté dans la situation reçue.">
          Le retrait doit porter le montant attendu, à 1 % près. Les ordres se soumettent puis se
          valident depuis l’écran Ordres.
        </Note>
      </Bloc>

      <Bloc titre="Historique" meta={`${soldees.length} demande(s) soldée(s)`}>
        <Tableau
          colonnes={colonnesSoldees}
          lignes={soldees}
          cle={(d) => d.id}
          vide="Aucune demande soldée."
        />
      </Bloc>

      <Modale
        ouverte={aAbandonner !== null}
        onFermer={() => setAAbandonner(null)}
        surtitre={aAbandonner ? `Mandat · ${aAbandonner.mandat.reference}` : undefined}
        titre={`Abandonner ${aAbandonner?.reference ?? ''}`}
      >
        {erreurAbandon && (
          <BandeauAlerte nature="refus" titre="Abandon refusé">
            {erreurAbandon}
          </BandeauAlerte>
        )}
        {aAbandonner?.statut === 'ORDRES_GENERES' && (
          <p className="consigne">
            Les ordres déjà générés ne sont pas annulés par l’abandon : retirez-les depuis l’écran
            Ordres s’ils n’ont pas encore été transmis.
          </p>
        )}
        <form onSubmit={(e) => void abandonner(e)} noValidate>
          <Champ
            label="Motif de l’abandon"
            value={motifAbandon}
            onChange={(e) => setMotifAbandon(e.target.value)}
            required
          />
          <div className="modale__actions">
            <button
              type="button"
              className="bouton bouton--fantome"
              onClick={() => setAAbandonner(null)}
            >
              Annuler
            </button>
            <button className="bouton" type="submit" disabled={occupe || !motifAbandon.trim()}>
              {occupe ? 'En cours…' : 'Abandonner la demande'}
            </button>
          </div>
        </form>
      </Modale>
    </>
  );
}
